import { transport, useTransportQuery } from "@/transport"

import type {
  BucketInterval,
  TimeBasis,
} from "@/types_api/backend/transport/get_metric_agreement_history_request"
import type { GetMetricAgreementHistoryResponse } from "@/types_api/backend/transport/get_metric_agreement_history_response"

/**
 * Reads machine-vs-human agreement for one metric, split across the versions its labels were
 * made under, with an optional bucketed series over the same window.
 *
 * Each conversation counts once, under the version its label was made under, so the per-version
 * rates are over different conversations and are not normalized against each other.
 *
 * @param timeBasis Whether the window and buckets follow when a conversation was labeled or
 *   when it was uploaded.
 */
export function useMetricAgreementHistoryTransport({
  metricId,
  startDate,
  endDate,
  timeBasis = "label",
  bucketInterval = "1 day",
  includeSeries = true,
  enabled = true,
}: {
  metricId?: string
  startDate: Date
  endDate?: Date
  timeBasis?: TimeBasis
  bucketInterval?: BucketInterval
  includeSeries?: boolean
  enabled?: boolean
}) {
  return useTransportQuery({
    operation: transport.operations.getMetricAgreementHistory,
    request: {
      metric_id: metricId ?? "",
      start_date: startDate.toISOString(),
      ...(endDate ? { end_date: endDate.toISOString() } : {}),
      time_basis: timeBasis,
      bucket_interval: bucketInterval,
      include_series: includeSeries,
    },
    options: {
      enabled: !!metricId && enabled,
      select: (response: GetMetricAgreementHistoryResponse) => ({
        byVersion: response.by_version ?? [],
        series: response.series ?? [],
        // Labels carrying no version. They are in the series but under no version, so summing
        // byVersion against the series total needs this to reconcile.
        unattributedConversations: response.unattributed_conversations ?? 0,
      }),
    },
  })
}
